import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getNavigationRoutes } from '../../../routes/routes';
import SidebarItem from './SidebarItem';
import useRoleAccess from '../../../hooks/useRoleAccess';
import { useModuleVisibility } from '../../../contexts/ModuleVisibilityContext';

const logoUrl = `${process.env.PUBLIC_URL || ''}/logo_icon.png`;

const STORAGE_KEY = 'savvpos.sidebar.collapsed';

// Ordered group definitions — each route is assigned to the FIRST group whose
// prefix matches. Routes that match nothing land in "Other" at the bottom.
const NAV_GROUPS = [
  { label: 'Main',           prefixes: ['/dashboard'] },
  { label: 'Sales',          prefixes: ['/sales'] },
  { label: 'Sales Products', prefixes: ['/products'] },
  { label: 'Production',     prefixes: ['/production', '/kds'] },
  { label: 'Inventory',      prefixes: ['/inventory'] },
  { label: 'Purchases',      prefixes: ['/purchases'] },
  { label: 'Transfers',      prefixes: ['/stock-transfers'] },
  { label: 'Reports',        prefixes: ['/reports', '/staff-performance'] },
  { label: 'Customers',      prefixes: ['/customers'] },
  { label: 'Suppliers',      prefixes: ['/suppliers'] },
  { label: 'Cashier',        prefixes: ['/cashier'] },
  { label: 'People',         prefixes: ['/staff', '/attendance', '/roles'] },
  { label: 'Shift',          prefixes: ['/shift-management'] },
  { label: 'Marketing',      prefixes: ['/marketing'] },
  { label: 'CRM',            prefixes: ['/crm'] },
  { label: 'Service',        prefixes: ['/service-tickets'] },
  { label: 'System',         prefixes: ['/settings', '/warehouses'] },
];

const groupForPath = (path) => {
  const p = String(path || '');
  const match = NAV_GROUPS.find((g) =>
    g.prefixes.some((prefix) => p === prefix || p.startsWith(prefix + '/'))
  );
  return match ? match.label : 'Other';
};

export default function Sidebar({ isMobile, mobileOpen, onCloseMobile }) {
  const location = useLocation();
  const { filterRoutes } = useRoleAccess();
  const { filterByVisibility } = useModuleVisibility();

  const routes = useMemo(() => {
    const base = getNavigationRoutes().filter((r) => !r.hideFromMenu);
    return filterRoutes(filterByVisibility(base));
  }, [filterByVisibility, filterRoutes]);

  // Group routes by section label, preserving the NAV_GROUPS order.
  const groupedRoutes = useMemo(() => {
    const buckets = {};
    routes.forEach((route) => {
      const label = groupForPath(route.path);
      if (!buckets[label]) buckets[label] = [];
      buckets[label].push(route);
    });
    // Build ordered sections: defined groups first (in order), then "Other".
    const sections = [];
    NAV_GROUPS.forEach(({ label }) => {
      if (buckets[label]?.length) {
        sections.push({ label, routes: buckets[label] });
      }
    });

    return sections;
  }, [routes]);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsedPersist = (next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }
  };

  const [expandedPath, setExpandedPath] = useState(() => location.pathname);

  const handleToggleExpanded = (path) => {
    setExpandedPath((prev) => (prev === path ? null : path));
  };

  const sidebarBody = (
    <div
      className={`relative flex h-screen flex-col bg-[#0061B2] text-white ${
        collapsed ? 'w-16' : 'w-64'
      } transition-[width] duration-300 ease-in-out shadow-2xl z-[100]`}
    >
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center gap-2.5 px-3 border-b border-white/10">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-lg shadow-black/10"
          aria-hidden
        >
          <img src={logoUrl} alt="Logo" className="h-full w-full object-cover" />
        </div>

        {!collapsed && (
          <span className="truncate text-sm font-bold tracking-tight text-white">
            TechSavanna
          </span>
        )}

        {!isMobile && (
          <button
            type="button"
            className="absolute -right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#0061B2] shadow-md border border-slate-200 hover:bg-slate-50 transition-all z-50 cursor-pointer"
            onClick={() => setCollapsedPersist(!collapsed)}
            aria-label="Toggle sidebar"
          >
            <span className="relative text-base leading-none" style={{ top: '-1px' }}>
              {collapsed ? '›' : '‹'}
            </span>
          </button>
        )}
      </div>

      {/* Nav — grouped sections, no duplicate headers */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 sidebar-scrollbar">
        {groupedRoutes.map(({ label, routes: sectionRoutes }, sectionIndex) => (
          <div key={label} className={sectionIndex > 0 ? 'mt-3' : ''}>
            {!collapsed && (
              <div className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                {label}
              </div>
            )}
            <div className="space-y-0.5">
              {sectionRoutes.map((route) => (
                <SidebarItem
                  key={route.path}
                  route={route}
                  active={
                    location.pathname === route.path ||
                    (route.path !== '/dashboard' && location.pathname.startsWith(route.path + '/'))
                  }
                  collapsed={collapsed}
                  expandedPath={expandedPath}
                  onToggleExpanded={handleToggleExpanded}
                  onClick={() => {
                    if (isMobile && onCloseMobile) onCloseMobile();
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );

  if (!isMobile) {
    return (
      <div className="h-full overflow-visible shrink-0 no-print">{sidebarBody}</div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 no-print ${mobileOpen ? '' : 'pointer-events-none'}`}>
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={onCloseMobile}
      />
      <div className={`absolute left-0 top-0 h-full transition-transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {sidebarBody}
      </div>
    </div>
  );
}
